import { Action, createReducer, on } from '@ngrx/store';

import { HistoryItem, HistoryValidator } from '@app/entities/router-state.entity';
import {
  historyPushAction, historyPopAction,
  historyResetAction, restoreHistorySuccessAction
} from '@app/store/actions/global.actions';
import { homeRootRoute } from '@home/home-routing.module';
import { hotelResultsRootRoute } from '@hotels/hotel-results/hotel-results-routing.module';
import { userHistoryDetailRoute, userHistoryRoute } from '@user-history/constants/history-routes.constants';
import { homeItemDetailRootRoute } from '@home-item-detail/home-item-detail-routing.module';
import { autoHomeRootRoute } from '@autos/auto-home/constants/routes';
import { packagesResultRootRoute, STEP_SECTION } from '@packages/packages-result/constants/packages-result.routes';
import {
  setHotelResultUrlInHotelDetailAction, setPackageBaseUrlResultInHotelDetailAction
} from '@hotels/hotel-detail/store/actions/hotels.actions';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { setHotelResultURL } from '@hotels/hotel-detail/mappers/url-results.mapper';
import { destinationIsInV2 } from '@packages/packages-search-bar/mappers/packages-url.mapper';

export const initialState: HistoryItem[] = [{
  url: homeRootRoute,
  extras: {}
}];

export const reducer = createReducer(
  initialState,
  on(historyPushAction, (state, { item }) => {
    const _state = state || initialState;
    if (item.url !== homeRootRoute) {
      if (isAutos(item.url)) {
        return historyPushModuleAutos(state, item);
      }
      if (item.url.includes(packagesResultRootRoute)) {
        const { replaceRoute, addNewItem } = getHistoryItemValidationElements(item, state);
        const sectionByLast = state?.[state?.length - 1]?.url.split('/');
        const sectionByCurrent = item.url.split('/');
        const replaceByRedirect = destinationIsInV2(sectionByLast?.[4]) && !destinationIsInV2(sectionByCurrent?.[4])
        return replaceRoute || replaceByRedirect ? replaceRouteState(item, state) : addNewItem ? [..._state, item] : _state;
      }
      if (item.url.startsWith(`/${userHistoryRoute}/${userHistoryDetailRoute}`)) {
        return [..._state, item];
      }
      return historyPushDefault(state, item);
    } else return _state;
  }),
  on(historyPopAction, (state) => {
    return state.length === 1 ? state : state.slice(0, -1);
  }),
  on(historyResetAction, () => initialState),
  on(setPackageBaseUrlResultInHotelDetailAction, (state, { url }) => {
    return setPackageBaseUrlResultInHotelDetailMapper(state, url);
  }),
  on(setHotelResultUrlInHotelDetailAction, (state, { url }) => {
    return setHotelResultUrlInHotelDetailMapper(state, url);
  }),
  on(restoreHistorySuccessAction, (state, { payload }) => {
    return payload;
  })
);

export const historyReducer = (state: HistoryItem[], action: Action): HistoryItem[] => {
  return reducer(state, action);
};

function historyPushModuleAutos(state: HistoryItem[], item: HistoryItem): HistoryItem[] {
  const _state = state || initialState;
  const isSameUrl = decodeURI(_state[_state.length - 1].url).includes(decodeURI(item.url));
  const isOutlet = item.url.includes('/(outlet:') || item.url.includes('/(search:');
  return isSameUrl || isOutlet ? _state : [..._state, item];
}

function historyPushDefault(state: HistoryItem[], item: HistoryItem): HistoryItem[] {
  const { rootItemUrl, lastRootItemUrl, replaceRoute, addNewItem } = getHistoryItemValidationElements(item, state);
  const _state = state || initialState;
  return rootItemUrl !== lastRootItemUrl ?
    replaceRoute ? replaceRouteState(item, state) : addNewItem ? [..._state, item] : _state : _state;
}

export function replaceRouteState(item: HistoryItem, state: HistoryItem[]): HistoryItem[] {
  return state?.map((itemState, index) => {
    if (index !== state.length - 1) {
      return itemState;
    } else {
      return item;
    }
  })
}

function setHotelResultUrlInHotelDetailMapper(state: HistoryItem[], url: string): HistoryItem[] {
  if (state?.length && state.find(item => item.url?.includes(hotelResultsRootRoute))) {
    const index = state.findIndex(item => item.url?.includes(hotelResultsRootRoute));
    const formatUrl: string = setHotelResultURL(state[index].url, url);
    return formatUrl ? [...state.slice(0, index), { ...state[index], url: formatUrl }, ...state.slice(index + 1)] : state;
  } else return state;
}

function setPackageBaseUrlResultInHotelDetailMapper(state: HistoryItem[], url: string): HistoryItem[] {
  if (state.length && state.find(item => item.url?.includes(packagesResultRootRoute))) {
    const index = state.findIndex(item => item.url?.includes(packagesResultRootRoute));
    const formatUrl: string = url.replace(STEP_SECTION, PackageStep.HOTEL_RESULTS);
    return [...state.slice(0, index), { ...state[index], url: formatUrl }, ...state.slice(index + 1)];
  } else return state;
}

function getHistoryItemValidationElements(item: HistoryItem, state: HistoryItem[]): HistoryValidator {
  const itemUrlSection = !!item.url && sectionUrl(item.url);
  const rootItemUrl = !!itemUrlSection && itemUrlSection[0];
  const lastItemHistory = !!state && state[state.length - 1];
  const lastUrlSection = !!lastItemHistory.url && sectionUrl(lastItemHistory.url);
  const lastRootItemUrl = !!lastUrlSection && lastUrlSection[0] || '';
  const replaceRoute = canReplaceRoute(rootItemUrl, lastRootItemUrl, item, lastItemHistory);
  const addNewItem = differentUrl(item.url, lastItemHistory.url);
  return { rootItemUrl, lastRootItemUrl, replaceRoute, addNewItem };
}

function sectionUrl(url: string): string[] {
  return !!url && url.split('/').filter(item => !!item);
}

function canReplaceRoute(rootItemUrl: string, lastRootItemUrl: string, item: HistoryItem, lastItemHistory: HistoryItem): boolean {
  return !!lastRootItemUrl && !excludeToReplace(lastRootItemUrl) &&
    (rootItemUrl === lastRootItemUrl && differentUrl(item.url, lastItemHistory.url));
}

function excludeToReplace(url: string): boolean {
  return url === userHistoryRoute || url === homeItemDetailRootRoute;
}

function differentUrl(currentUrl: string, previewUrl: string): boolean {
  if (currentUrl || previewUrl) return true;
  if (currentUrl.slice(-1) === homeRootRoute) return false;
  else {
    if (previewUrl.slice(-1) === homeRootRoute) return true;
    else {
      const current = `${currentUrl}/`;
      const url = `${previewUrl}/`;
      return !current.includes(url);
    }
  }
}

function isAutos(currentUrl: string): boolean {
  return currentUrl.includes(autoHomeRootRoute);
}
